import React, { useEffect, useState } from 'react'
import { useParams, Route } from 'react-router-dom'
import { useRouteMatch, Switch } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import cx from 'classnames'
import Page from '../../common/Page'
import $t from '../../../i18n'
import CardListItem from '../../common/CardListItem'
import Actions from '../../../actions'
import { getUrlParams } from 'utils/useParamUtil'
import { CARD_LIST_ITEM_IMAGE } from 'consts/CardListItemConsts'
import { CLAIM_TYPE, CLAIM_STATUS_CODES } from 'consts/ClaimsConsts'
import {
  getClaimFormattedDate,
  formattedClaimId,
  getClaimStatusByCode
} from '../../../utils/claims'
import { capitalizeFirstLetter, capitalizeFirstLetterEachWord } from 'consts'
import FilePreviewPage from '../../pages/FilePreviewPage'
import { getDocLink } from 'utils/coverageBenefitsUtils'
import { CoverageBenefitsHMODocuments } from 'consts'
import CollapseDetails from '../../common/CollapseDetails'
import { formatPrice } from 'utils/hmoUtils'
import Alert from '../../common/Alert'
import EmptyList from '../../common/EmptyList'
import './SingleClaim.css'

const SingleClaim = () => {
  const { claimId } = useParams()
  const { memberId } = getUrlParams()
  const { path, url } = useRouteMatch()
  const dispatch = useDispatch()

  const {
    isClaimDetailsLoading,
    claimDetails,
    eobBlobFile,
    isMemberEobLoading
  } = useSelector(state => state?.nhMember)

  const authenticatedLinks = useSelector(
    state => state.profile?.authenticatedLinks
  )
  const userHomeAddressState = useSelector(
    state => state.account?.userHomeAddressState
  )

  const { isClaimEobFileViewFailed } = useSelector(state => state?.nhMember)

  const [appealProcessDocLink, setAppealProcessDocLink] = useState(null)
  const [isFaildPopupVisibale, setIsFaildPopupVisibale] = useState(null)
  const isMedicalClaim =
    claimDetails?.claimType?.toLowerCase() === CLAIM_TYPE.MEDICAL

  useEffect(() => {
    if (
      authenticatedLinks &&
      userHomeAddressState &&
      claimDetails?.claimStatusCode === CLAIM_STATUS_CODES.DENIED
    ) {
      setAppealProcessDocLink(
        getDocLink({
          authenticatedLinks,
          userHomeAddressState,
          documentName: CoverageBenefitsHMODocuments.APPEALS_PROCESS
        })
      )
    }
  }, [authenticatedLinks, userHomeAddressState, claimDetails?.claimStatusCode])

  useEffect(() => {
    if (claimId && memberId) {
      dispatch(
        Actions.createAction(Actions.CLAIM_DETAILS_REQUESTED, {
          claimId,
          memberId
        })
      )
    }
  }, [claimId, memberId, dispatch])

  useEffect(() => {
    if (eobBlobFile) {
      dispatch(Actions.createAction(Actions.NAVIGATED_TO, `${url}/eob_file/0`))
    }
  }, [eobBlobFile, isMemberEobLoading, dispatch, url])

  useEffect(() => {
    if (isClaimEobFileViewFailed && !eobBlobFile) {
      setIsFaildPopupVisibale(isClaimEobFileViewFailed)
    }
  }, [isClaimEobFileViewFailed, eobBlobFile])

  const onClickEob = () => {
    dispatch(
      Actions.createAction(Actions.MEMBER_EOB_REQUESTED, {
        claimId,
        memberId
      })
    )
  }

  const getFormattedValue = value => {
    const formattedValue = formatPrice(value)
    if (formattedValue != null) {
      return `$${formattedValue}`
    }
    return '-'
  }

  const getFileAttachments = () => {
    const shouldShowEobSection = isMedicalClaim
    const hasEob = claimDetails?.eboFileName
    const shouldRemoveAttachmentsSection =
      !shouldShowEobSection && !appealProcessDocLink

    const eobSection = shouldShowEobSection ? (
      <>
        {hasEob ? (
          <div className='file-link-container'>
            <i className='icon-paper-clip' />
            <span className='link' onClick={onClickEob}>
              {$t('pages.claimDetails.eob')}
            </span>
          </div>
        ) : (
          <>{$t('pages.claimDetails.eobAvailableSoon')}</>
        )}
      </>
    ) : null

    const appealProcessSection = appealProcessDocLink ? (
      <div className='file-link-container'>
        <i className='icon-paper-clip' />
        <span
          className='link'
          onClick={() => window.open(appealProcessDocLink)}
        >
          {$t('pages.claimDetails.appealProcess')}
        </span>
      </div>
    ) : null

    return shouldRemoveAttachmentsSection ? null : (
      <div className='single-claim-details-container-item'>
        <span className='single-claim-details-container-item-label'>
          {$t('pages.claimDetails.attachedDocuments')}:
        </span>
        <span className='single-claim-details-container-item-value'>
          {eobSection}
          {appealProcessSection}
        </span>
      </div>
    )
  }

  const attachmentsSection = getFileAttachments()

  return (
    <Switch>
      <Route path={`${path}/eob_file/0`}>
        {eobBlobFile ? (
          <FilePreviewPage
            className='claims-page'
            pdfBlob={eobBlobFile}
            isDownloadEnable={true}
          />
        ) : null}
      </Route>
      <Route path={path}>
        <Page
          loading={isClaimDetailsLoading || isMemberEobLoading}
          className='single-claim-page'
          fullWidth={true}
          title={$t('pages.claimDetails.title')}
        >
          {!claimDetails ? (
            <EmptyList title={$t('pages.claimDetails.noClaimDetails')} />
          ) : (
            <>
              <CardListItem
                className={'card-list-item trackable'}
                data-name={claimDetails?.dataName}
                issueDate={`${$t(
                  'pages.claims.serviceDate'
                )}: ${getClaimFormattedDate(claimDetails?.dateOfService)}`}
                title={
                  capitalizeFirstLetterEachWord(claimDetails?.providerName) ||
                  $t('pages.claims.providerName')
                }
                subtitle={capitalizeFirstLetter(
                  `${claimDetails?.claimType} ${$t(
                    'pages.claims.claimNumber'
                  )}${formattedClaimId(claimDetails?.claimId)}`
                )}
                imageName={
                  isMedicalClaim
                    ? CARD_LIST_ITEM_IMAGE.PROVIDER
                    : CARD_LIST_ITEM_IMAGE.PHARMACY
                }
                isLoading={isClaimDetailsLoading}
                dataName={claimDetails?.dataName}
                status={getClaimStatusByCode(claimDetails?.claimStatusCode)}
                index={claimId}
              >
                <div
                  className={cx(
                    'single-claim-details-container',
                    attachmentsSection ? 'arrow-space-saver' : '',
                    attachmentsSection ? '' : 'no-attachments-section'
                  )}
                >
                  <div className='single-claim-details-container-item-first'>
                    <div className='single-claim-details-container-item'>
                      <span className='single-claim-details-container-item-label'>
                        {isMedicalClaim
                          ? `${$t('pages.claimDetails.providerNPI')}:`
                          : `${$t('pages.claimDetails.medicationName')}:`}
                      </span>
                      <span className='single-claim-details-container-item-value'>
                        {isMedicalClaim
                          ? claimDetails?.providerNpi || '-'
                          : capitalizeFirstLetter(
                              claimDetails?.medicationName
                            ) || '-'}
                      </span>
                    </div>
                  </div>
                  <div className='single-claim-details-container-item-second'>
                    <div className='single-claim-details-container-item'>
                      <span className='single-claim-details-container-item-label'>
                        {$t('pages.claimDetails.claimReceived')}:
                      </span>
                      <span className='single-claim-details-container-item-value'>
                        {getClaimFormattedDate(claimDetails?.receiptDate)}
                      </span>
                    </div>
                    {attachmentsSection}
                  </div>
                </div>
              </CardListItem>

              {claimDetails?.serviceLines?.length > 0 ? (
                <div className='single-claim-services-container'>
                  <h2 className='single-claim-services-container-header'>
                    {isMedicalClaim
                      ? `${$t('pages.claimDetails.services')}:`
                      : `${$t('pages.claimDetails.payments')}:`}
                  </h2>
                  {claimDetails?.serviceLines?.map(service => {
                    const headerComponent = (
                      <div
                        className='single-claim-details-container'
                        key={`service-${service?.serviceCode}-header`}
                      >
                        {isMedicalClaim ? (
                          <>
                            <div className='single-claim-details-container-item-first'>
                              <div className='single-claim-details-container-item'>
                                <span className='single-claim-details-container-item-label'>
                                  {$t('pages.claimDetails.service')}:
                                </span>
                                <span className='single-claim-details-container-item-value'>
                                  {capitalizeFirstLetter(
                                    service?.displayTitle?.toLowerCase()
                                  ) || '-'}
                                </span>
                              </div>
                            </div>
                            <div className='single-claim-details-container-item-second'>
                              <div className='single-claim-details-container-item'>
                                <span className='single-claim-details-container-item-label'>
                                  {$t('pages.claimDetails.status')}:
                                </span>
                                <span className='single-claim-details-container-item-value'>
                                  {capitalizeFirstLetter(
                                    service?.status?.toLowerCase()
                                  )}{' '}
                                  {service?.claimStatusCode ===
                                  CLAIM_STATUS_CODES.PAID
                                    ? `| ${getClaimFormattedDate(
                                        service?.checkDate
                                      )}`
                                    : ''}
                                </span>
                              </div>
                              <div className='single-claim-details-container-item'>
                                <span className='single-claim-details-container-item-label'>
                                  {$t(
                                    'pages.claimDetails.memberResponsibility'
                                  )}
                                  :
                                </span>
                                <span className='single-claim-details-container-item-value'>
                                  {getFormattedValue(
                                    service?.memberAmount ||
                                      service?.totalAmountOwedByMember
                                  )}
                                </span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className='single-claim-details-container-item-first'>
                            <div className='single-claim-details-container-item'>
                              <span className='single-claim-details-container-item-label'>
                                {$t('pages.claimDetails.memberResponsibility')}:
                              </span>
                              <span className='single-claim-details-container-item-value'>
                                {getFormattedValue(
                                  service?.memberAmount ||
                                    service?.totalAmountOwedByMember
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )

                    const contentComponent = (
                      <div
                        className='single-claim-service-details-container'
                        key={`service-${service?.serviceCode}-content`}
                      >
                        <span className='single-claim-service-details-label'>
                          {$t('pages.claimDetails.paymentBreakdown')}
                        </span>
                        <div className='single-claim-service-payment-details'>
                          <div className='single-claim-service-payment-details-item'>
                            <span>
                              {$t('pages.claimDetails.billedAmount')}:
                            </span>
                            <span>
                              {getFormattedValue(service.billedAmount)}
                            </span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>
                              {$t('pages.claimDetails.contractualAdjustment')}:
                            </span>
                            <span>
                              {getFormattedValue(service.planDiscount)}
                            </span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>
                              {$t('pages.claimDetails.allowedAmount')}:
                            </span>
                            <span>
                              {getFormattedValue(service.allowedAmount)}
                            </span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>{$t('pages.claimDetails.planPaid')}:</span>
                            <span>{getFormattedValue(service.paidAmount)}</span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>{$t('pages.claimDetails.copay')}:</span>
                            <span>{getFormattedValue(service.copay)}</span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>{$t('pages.claimDetails.coinsurance')}:</span>
                            <span>
                              {getFormattedValue(service.coinsurance)}
                            </span>
                          </div>
                          <div className='single-claim-service-payment-details-item'>
                            <span>{$t('pages.claimDetails.deductible')}:</span>
                            <span>
                              {getFormattedValue(service.deductibleAmount)}
                            </span>
                          </div>
                        </div>
                        {isMedicalClaim ? (
                          <div className='single-claim-service-details-container'>
                            <span className='single-claim-service-details-label'>
                              {$t('pages.claimDetails.serviceDescription')}
                            </span>
                            <div className='single-claim-service-description-value'>
                              <div>
                                {$t('pages.claimDetails.code')}:{' '}
                                {service.serviceCode}
                              </div>
                              {service.displayDescription}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )

                    const itemKey = `service-${service?.serviceCode}`

                    return (
                      <CollapseDetails
                        key={itemKey}
                        itemKey={itemKey}
                        headerComponent={headerComponent}
                        contentComponent={contentComponent}
                        defaultActiveKey={
                          claimDetails?.claimType === CLAIM_TYPE.PHARMACY
                            ? `${itemKey}-panel`
                            : null
                        }
                      />
                    )
                  })}
                </div>
              ) : null}
              <Alert
                title={$t('common.note')}
                className='redirect-alert'
                closable={true}
                shouldDisplay={isFaildPopupVisibale}
                onCancel={() => {
                  setIsFaildPopupVisibale(false)
                }}
                OkText={$t('common.ok')}
                onOk={() => {
                  setIsFaildPopupVisibale(false)
                }}
              >
                {$t('pages.claimDetails.eobFileUnavailable')}
              </Alert>
            </>
          )}
        </Page>
      </Route>
    </Switch>
  )
}

export default SingleClaim
